import { H3 } from '@common/headline';
import { ImageCard } from '@common/ImageCard';

import { getRecentArticles } from '@nextUtils/guidesUtils';

import { RecentArticleEdgeType } from '@nextTypes/guides';

type Props = {
  currentSlug: string;
  posts: RecentArticleEdgeType[];
};

export const SuccessStorySuggestions = ({
  currentSlug,
  posts,
}: Props): JSX.Element | null => {
  if (posts.length === 0) {
    return null;
  }

  // For our rebrand launch, we specifically want these three
  // articles as they've been designed to fit the rebrand guidelines
  const similarArticlesForRebrand = posts.filter(article =>
    [
      'An interview with Karen',
      'An interview with Carla',
      'An interview with Anne',
    ].includes(article.node.title),
  );

  const similarArticleTiles = getRecentArticles({
    edges: similarArticlesForRebrand,
  }).map(article => (
    <div
      className="hidden first:block last:mr-0 md:mr-6 md:even:block xl:last:block"
      key={article.title}
    >
      <ImageCard
        image={article.featuredImageUrl}
        altText={article.title}
        title={article.title}
        subtitle={article.description}
        href={article.href}
      />
    </div>
  ));

  // We want to ensure that when on mobile, we don't suggest the current article
  // so we need to filter it out and then only show the first article
  const similarArticlesWithoutCurrentArticle = similarArticlesForRebrand.filter(
    post => post.node.slug !== currentSlug,
  );

  // If the only similar article is the current article, don't show any suggestions
  if (similarArticlesWithoutCurrentArticle.length === 0) {
    return null;
  }

  // Randomly pick between the other similar articles
  // and then choose the first result
  const articleSuggestionForMobile = getRecentArticles({
    edges: similarArticlesWithoutCurrentArticle
      .sort(() => 0.5 - Math.random())
      .slice(0, 1),
  }).map(article => (
    <ImageCard
      image={article.featuredImageUrl}
      altText={article.title}
      title={article.title}
      subtitle={article.description}
      href={article.href}
      key={article.title}
    />
  ));

  return (
    <div className="w-full bg-blue px-3 pb-[72px] pt-16 text-center text-mono-white lg:px-[60px] lg:py-[104px]">
      <H3>
        You might also <span className="text-teal">like</span>
      </H3>
      <div className="mt-10 hidden flex-row justify-center text-left text-blue md:flex">
        {similarArticleTiles}
      </div>
      <div className="mt-10 flex flex-row justify-center text-left text-blue md:hidden">
        {articleSuggestionForMobile}
      </div>
    </div>
  );
};
